import React, { useCallback, useEffect, useRef, useState } from 'react'
import { GoogleMap, InfoWindowF, Marker, PolylineF, useJsApiLoader } from '@react-google-maps/api'
import Loader from '../UI/Loader'
import { IMarker } from '../../models/models'
import truckFull from '../../assets/icons/truck-full.svg'
import truckMid from '../../assets/icons/truck-mid.svg'
import truckLow from '../../assets/icons/truck-low.svg'
import trailerFullFull from '../../assets/icons/trailer-full-full.svg'
import trailerFullMid from '../../assets/icons/trailer-full-mid.svg'
import trailerFullLow from '../../assets/icons/trailer-full-low.svg'
import trailerMidFull from '../../assets/icons/trailer-mid-full.svg'
import trailerMidMid from '../../assets/icons/trailer-mid-mid.svg'
import trailerMidLow from '../../assets/icons/trailer-mid-low.svg'
import trailerLowFull from '../../assets/icons/trailer-low-full.svg'
import trailerLowMid from '../../assets/icons/trailer-low-mid.svg'
import trailerLowLow from '../../assets/icons/trailer-low-low.svg'
import flag from '../../assets/icons/flag.svg'
import point from '../../assets/icons/point.svg'
import gas from '../../assets/icons/gas-station.svg'
import Button from '../UI/Button'
import s from './index.module.scss'
import dateFormat from 'dateformat'

const decodePolyline = require('decode-google-map-polyline')

type Libraries = ('drawing' | 'geometry' | 'localContext' | 'places' | 'visualization')[]

const libraries: Libraries = ['places']

const center = {
	lat: 50.450001,
	lng: 30.523333,
}

const options: google.maps.MapOptions = {
	zoomControl: false,
	streetViewControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
	maxZoom: 18,
}

const polylineOptions: google.maps.PolylineOptions = {
	clickable: false,
	draggable: false,
	editable: false,
	strokeColor: '#ED1B24',
	strokeWeight: 4,
}

const Map = ({
	markers = [],
	polylines,
}: {
	markers?: IMarker[]
	polylines?: string[]
}) => {
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
		libraries: libraries,
		language: 'UK',
	})

	const firstRender = useRef(true)
	const mapRef = useRef<google.maps.Map | null>(null)

	const [activeMarker, setActiveMarker] = useState<number | string | null>(0)

	const onLoad = useCallback((map: google.maps.Map) => {
		mapRef.current = map
	}, [])

	const onUnmount = useCallback((map: google.maps.Map) => {
		mapRef.current = null
	}, [])

	useEffect(() => {
		if (!firstRender.current) return

		if (mapRef.current && markers.length && firstRender.current) {
			const bounds = new window.google.maps.LatLngBounds()

			markers.map((marker) => {
				bounds.extend({
					lat: marker.point.latitude,
					lng: marker.point.longitude,
				})
			})

			mapRef.current.fitBounds(bounds)
			firstRender.current = false
		}
	}, [markers])

	if (!isLoaded) {
		return <Loader />
	}

	return (
		<div style={{ height: '100%' }}>
			<GoogleMap
				mapContainerStyle={{
					width: '100%',
					height: '100%',
				}}
				center={center}
				zoom={10.5}
				onLoad={onLoad}
				onUnmount={onUnmount}
				options={options}
			>
				{markers.map((i, index) => {
					let icon: any = ''

					if (i.type === 'truck') {
						if (i.percentage <= 25) {
							icon = truckLow
						} else if (i.percentage <= 50) {
							icon = truckMid
						} else {
							icon = truckFull
						}
					} else if (i.type === 'trailer' && i.trailer?.hasOwnProperty('percentage')) {
						const trailerPercentage = i.trailer.percentage

						if (i.percentage === null) {
							if (trailerPercentage <= 25) {
								icon = truckLow
							} else if (trailerPercentage <= 50) {
								icon = truckMid
							} else {
								icon = truckFull
							}
						} else if (i.percentage <= 25) {
							if (trailerPercentage <= 25) {
								icon = trailerLowLow
							} else if (trailerPercentage <= 50) {
								icon = trailerLowMid
							} else {
								icon = trailerLowFull
							}
						} else if (i.percentage <= 50) {
							if (trailerPercentage <= 25) {
								icon = trailerMidLow
							} else if (trailerPercentage <= 50) {
								icon = trailerMidMid
							} else {
								icon = trailerMidFull
							}
						} else {
							if (trailerPercentage <= 25) {
								icon = trailerFullLow
							} else if (trailerPercentage <= 50) {
								icon = trailerFullMid
							} else {
								icon = trailerFullFull
							}
						}
					} else if (i.type === 'point') {
						icon = point
					} else if (i.type === 'flag') {
						icon = flag
					} else if (i.type === 'gas') {
						icon = gas
					}

					return (
						<>
							<Marker
								key={index}
								icon={
									i.type === 'order'
										? {
												path: 'M4,12.5a8.5,8.5 0 1,0 17,0a8.5,8.5 0 1,0 -17,0',
												fillColor: i.status.color,
												fillOpacity: 1,
												strokeWeight: 0,
											}
										: icon
								}
								position={{ lat: i.point.latitude, lng: i.point.longitude }}
								clickable={['truck', 'trailer', 'gas', 'order'].includes(i.type)}
								onClick={() => setActiveMarker(i.id)}
								draggable={i.type === 'flag' && i.draggable}
								onDragEnd={(ev) =>
									i.type === 'flag' &&
									i?.callback &&
									i.callback({
										latitude: ev.latLng?.lat() || 0,
										longitude: ev.latLng?.lng() || 0,
									})
								}
							>
								{(i.type === 'truck' || i.type === 'trailer' || i.type === 'gas' || i.type === 'order') && activeMarker === i.id && (
									<InfoWindowF onCloseClick={() => setActiveMarker(null)}>
										<div className={s.infoWrapper}>
											{(i.type === 'truck' || i.type === 'trailer') && (
												<>
													{i?.truckerFio && <span className={s.infoFio}>{i.truckerFio}</span>}
													{i.type === 'trailer' && <span className={s.infoTitle}>{i.isTractorUnit ? 'Тягач' : 'Паливовоз'}:</span>}
													<span>{i.number}</span>
													{!i.isTractorUnit && <span className={s.infoVolume}>Залишок: {i.amount} л</span>}
													{!i.isTractorUnit && i?.adBlueAmount ? (
														<span className={s.infoVolume} style={{ marginTop: '-10px' }}>
															Залишок AdBlue: {i.adBlueAmount} л
														</span>
													) : null}
												</>
											)}

											{i.type === 'trailer' && (
												<>
													<span className={s.infoTitle}>Причеп:</span>
													<span>{i.trailer?.number}</span>
													<span className={s.infoVolume}>Залишок: {i.trailerAmount} л</span>
												</>
											)}

											{i.type === 'gas' && <span className={s.infoName}>{i.name}</span>}

											{i.type === 'order' && (
												<>
													<span className={s.infoName}>ID: {i?.orderId}</span>
													{!!i?.products?.length &&
														i.products.map((j, index) => (
															<span key={index} className={s.infoName}>
																<b>{j?.name}:</b> {j?.ordered}л
															</span>
														))}
													{i?.truck && <span className={s.infoName}>Паливовоз: {i?.truck?.number}</span>}
													<span className={s.infoName}>Тип: {i.orderType}</span>
													<span className={s.infoName}>Дата/час: {dateFormat(i.orderDate, 'dd.mm.yyyy HH:MM')}</span>
												</>
											)}

											{i?.callback && (
												<Button
													variant="filled"
													onClick={() => {
														if (i?.callback) {
															i.type === 'order' ? i.callback(i.type === 'order' ? i.orderId : 0) : i.callback()
														}
														setActiveMarker(null)
													}}
												>
													{i.type === 'order' ? 'Переглянути' : 'Обрати'}
												</Button>
											)}
										</div>
									</InfoWindowF>
								)}
							</Marker>
						</>
					)
				})}

				{!!polylines?.length && polylines.map((i) => <PolylineF key={i} path={decodePolyline(i)} options={polylineOptions} />)}
			</GoogleMap>
		</div>
	)
}

export default React.memo(Map)
